import {
  Box,
  Typography,
  TextField as TextInput,
  Button,
  FormControl,
  MenuItem,
  Select,
  InputLabel,
  Card,
  CardContent,
  Autocomplete,
} from "@mui/material";
import { settings } from "app";

// Option text: the field's `optionText` (a key or a function), otherwise nameBg / name.
const optionLabel = (filter, option) => {
  if (!option) return "";
  if (typeof filter.optionText === "function") return String(filter.optionText(option) ?? "");
  const text = filter.optionText ? option[filter.optionText] : option.nameBg || option.name;
  return text != null ? String(text) : "";
};

const CustomFilter = ({
  filters = [],
  title,
  subTitle,
  buttonLabel,
  buttonDisabled = false,
  buttonOnClick,
  btnReset = false,
  onReset,
  defaultValue,
  cardWidth,
  titleClasses,
  buttonVariant = "contained",
  columns = 2,
}) => {
  // Fields sit `columns` per row on desktop and one per row below md; a single field never leaves an empty column.
  const desktopColumns = Math.max(1, Math.min(columns, filters.length));
  const gridSx = {
    display: "grid",
    gridTemplateColumns: { xs: "1fr", md: `repeat(${desktopColumns}, minmax(0, 1fr))` },
    // The action button takes its own full-width row.
    "& > .MuiButton-root": { gridColumn: "1 / -1", justifySelf: "center" },
  };
  // The fixed min widths only keep a lone field from collapsing; in a multi-column grid the cells size the fields.
  const fieldMinWidth = minWidth => (desktopColumns > 1 ? { minWidth: { xs: minWidth, md: 0 } } : { minWidth });

  const handleKeyDown = e => {
    // При отворен autocomplete оставяме Enter да избере маркираната опция,
    // вместо да прихващаме за search (фокусът остава в input-а вътре в Card-а).
    if (e.target?.getAttribute?.("aria-expanded") === "true") return;

    if (e.key === "Enter" && !btnReset && !buttonDisabled && buttonOnClick) {
      e.preventDefault();
      e.stopPropagation();
      buttonOnClick();
    }
  };

  return (
    <Box sx={settings.style.InputCardStyle}>
      {/* minWidth: 0 lets the card shrink below cardWidth when the screen is narrower. */}
      <Card
        onKeyDownCapture={handleKeyDown}
        sx={{ maxWidth: "100%", minWidth: 0 }}
      >
        {title && (
          <Typography
            variant="h5"
            sx={{ margin: "2rem 0rem 0rem", textAlign: "center", ...titleClasses }}
          >
            {title}
          </Typography>
        )}
        {subTitle && (
          <Typography
            variant="subtitle2"
            sx={{ margin: "0.5rem 0.9rem", textAlign: "center" }}
          >
            {subTitle}
          </Typography>
        )}
        <CardContent
          sx={{
            ...settings.style.CardContentStyle,
            ...gridSx,
            // cardWidth is the preferred width; never wider than the available space
            // (minus CardContentStyle's 1rem side margins). No boxSizing override needed: react-admin's
            // ScopedCssBaseline already makes everything border-box, so cardWidth has always included
            // the 1.5rem padding - this cap changes nothing where the card fits.
            width: cardWidth,
            maxWidth: "calc(100% - 2rem)",
          }}
        >
          {filters.map((filter, i) => {
            if (filter.type === "select") {
              return (
                <FormControl key={i}>
                  <InputLabel
                    id="select-label"
                    // With an empty option the label must stay up, or it overlaps the empty option's text.
                    shrink={filter.emptyText ? true : undefined}
                    sx={{
                      paddingX: "2px",
                    }}
                  >
                    {filter.label}
                  </InputLabel>
                  <Select
                    labelId="select-label"
                    sx={fieldMinWidth("280px")}
                    onChange={e => {
                      filter.setValue(e.target.value);
                    }}
                    defaultValue={filter.defaultValue}
                    value={filter.value}
                    // `emptyText` adds an empty ("") option, like react-admin's SelectInput, so the field can be cleared.
                    displayEmpty={Boolean(filter.emptyText)}
                  >
                    {filter.emptyText && <MenuItem value="">{filter.emptyText}</MenuItem>}
                    {filter.choices.map((choice, index) => {
                      return (
                        <MenuItem
                          key={index}
                          value={choice.id}
                        >
                          {optionLabel(filter, choice)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              );
            }
            if (filter.type === "autocomplete") {
              return (
                <FormControl key={i}>
                  <Autocomplete
                    multiple={filter.multiple}
                    sx={{ ...fieldMinWidth("280px"), marginTop: "-8px" }}
                    options={filter.choices || []}
                    getOptionLabel={option => optionLabel(filter, option)}
                    noOptionsText="Няма намерени резултати"
                    value={
                      filter.multiple
                        ? filter.choices?.filter(option => filter.value?.includes(option.id)) || []
                        : // String compare: numeric ids keep working, non-numeric (GUID) ids no longer turn into NaN.
                          filter.choices?.find(
                            option => filter.value != null && String(option.id) === String(filter.value),
                          ) || null
                    }
                    onChange={(_, value) => {
                      if (filter.multiple) {
                        filter.setValue(value.map(v => v.id));
                      } else {
                        filter.setValue(value?.id || null);
                      }
                    }}
                    // `onInputChange` = the options are searched on the server, so they are shown as they come.
                    onInputChange={
                      filter.onInputChange &&
                      ((_, value, reason) => {
                        // Picking an option "resets" the input to its text - keep the search that found it.
                        if (reason === "reset" && value) return;
                        filter.onInputChange(value);
                      })
                    }
                    filterOptions={
                      filter.filterOptions ??
                      (filter.onInputChange
                        ? options => options
                        : (options, { inputValue }) => {
                            const normalizedInput = String(inputValue).toLowerCase();
                            return options.filter(option => {
                              const text = optionLabel(filter, option);
                              return text && text.toLowerCase().includes(normalizedInput);
                            });
                          })
                    }
                    renderOption={(props, option) => (
                      <li
                        {...props}
                        key={option.id}
                      >
                        {optionLabel(filter, option)}
                      </li>
                    )}
                    isOptionEqualToValue={(option, value) => {
                      return option?.id === value?.id; // Ensures correct selection
                    }}
                    disabled={filter?.disable || false}
                    renderInput={params => (
                      <TextInput
                        {...params}
                        label={filter.label}
                      />
                    )}
                  />
                </FormControl>
              );
            }
            if (filter.type === "number") {
              return (
                <TextInput
                  key={i}
                  type="number"
                  sx={fieldMinWidth("150px")}
                  label={filter.label}
                  value={filter.value}
                  variant="filled"
                  onWheel={e => e.target.blur()}
                  onChange={e => {
                    filter.setValue(e.target.value);
                  }}
                />
              );
            }
            return (
              <TextInput
                sx={fieldMinWidth("150px")}
                key={i}
                label={filter.label}
                value={filter.value}
                variant="filled"
                onChange={e => {
                  filter.setValue(e.target.value);
                }}
              />
            );
          })}

          {!btnReset && (
            <Button
              variant={buttonVariant}
              color="primary"
              onClick={buttonOnClick}
              disabled={buttonDisabled}
              sx={{ minWidth: "120px", height: "min-content", paddingY: "0.5rem", margin: "auto" }}
            >
              {buttonLabel || "Провери"}
            </Button>
          )}
          {btnReset && (
            <Button
              variant={buttonVariant}
              color="info"
              onClick={onReset}
            >
              {"X Изчисти"}
            </Button>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default CustomFilter;
